import React, {useState, useEffect, useRef} from 'react';
import DatePicker from "react-datepicker";
import {formatDateToAmericanFormat, UserPermission, CompanyPermission} from "../../../utils/utils";
import {getCustomerAgeRangeReport, getAvailableAgeRanges} from "../../../utils/requestUtils";
import Config from "../../../core/config";
import {useRecoilState} from "recoil";
import Cookies from "js-cookie";
import {useNavigate} from "react-router-dom";
import 'react-datepicker/dist/react-datepicker.css';
import Loading from "../../../core/Loading";
import {loadingAtom} from "../../../atoms";
import {usePermission} from "../../../core/PermissionContext";
import WhatsAppButton from "../../../core/WhatsAppButton";

const apiUrl = Config.apiUrl;

const ReportCustomerAgeRange = (props) => {
    const {hasUserPermission, hasCompanyPermission, loadingPermissions} = usePermission();
    const [customers, setCustomers] = useState([]);
    const [ageRanges, setAgeRanges] = useState([]);
    const [selectedAgeRange, setSelectedAgeRange] = useState('');
    const [minAge, setMinAge] = useState('');
    const [maxAge, setMaxAge] = useState('');
    const [sortConfig, setSortConfig] = useState({key: 'kidAge', direction: 'ascending'});
    const [sortedCustomers, setSortedCustomers] = useState([]);
    const navigate = useNavigate();
    const [loading, setLoading] = useRecoilState(loadingAtom);

    async function makeQuery() {
        try {
            setLoading(true);
            const response = await getCustomerAgeRangeReport(
                minAge ? parseInt(minAge) : null,
                maxAge ? parseInt(maxAge) : null,
                selectedAgeRange
            );
            setCustomers(response);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    }

    async function loadAgeRanges() {
        try {
            const response = await getAvailableAgeRanges();
            setAgeRanges(response);
        } catch (error) {
            console.error('Error loading age ranges:', error);
        }
    }

    useEffect(() => {
        loadAgeRanges();
        makeQuery();
    }, []);

    useEffect(() => {
        setSortedCustomers([...customers].sort((a, b) => {
            const aValue = a[sortConfig.key];
            const bValue = b[sortConfig.key];

            if (aValue < bValue) {
                return sortConfig.direction === 'ascending' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === 'ascending' ? 1 : -1;
            }
            return 0;
        }));
    }, [customers, sortConfig]);

    const handleSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({key, direction});
    };

    const handleQuery = async (e) => {
        if (e) e.preventDefault();
        await makeQuery();
    };

    const getSortIcon = (key) => {
        if (sortConfig.key === key) {
            return sortConfig.direction === 'ascending' ? '▲' : '▼';
        }
        return '';
    };

    if (loadingPermissions) {
        return <Loading/>;
    }

    if (!hasUserPermission(UserPermission.REPORT_CUSTOMER_AGE_RANGE) || !hasCompanyPermission(CompanyPermission.REPORT_CUSTOMER_AGE_RANGE)) {
        navigate('/unauthorizedaccess');
        return null;
    }

    return (
        <>
            <div style={{display: "flex", alignItems: "center", gap: '10px'}}>
                <div className="header-label">Relatório de Clientes por Faixa Etária</div>
            </div>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', width: '100%'}}>
                <div style={{marginTop: '5px', alignItems: 'center', display: "flex"}}>
                    <div style={{flexDirection: "column", alignItems: 'center'}}>
                        Faixa Etária:
                        <div style={{display: 'flex', alignItems: 'center'}}>
                            <select
                                value={selectedAgeRange}
                                onChange={(e) => setSelectedAgeRange(e.target.value)}
                            >
                                <option value="">Todas as faixas</option>
                                {ageRanges.map((range, index) => (
                                    <option key={index} value={range}>{range}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div style={{flexDirection: "column", alignItems: 'center'}}>
                        Idade Mínima:
                        <div style={{display: 'flex', alignItems: 'center'}}>
                            <input
                                type="number"
                                value={minAge}
                                onChange={(e) => setMinAge(e.target.value)}
                                style={{width: '80px'}}
                                min="0"
                                max="18"
                                placeholder="Min"
                            />
                        </div>
                    </div>
                    <div style={{flexDirection: "column", alignItems: 'center'}}>
                        Idade Máxima:
                        <div style={{display: 'flex', alignItems: 'center'}}>
                            <input
                                type="number"
                                value={maxAge}
                                onChange={(e) => setMaxAge(e.target.value)}
                                style={{width: '80px'}}
                                min="0"
                                max="18"
                                placeholder="Max"
                            />
                            <button onClick={handleQuery}>Consultar</button>
                        </div>
                    </div>
                </div>
            </div>

            <div style={{marginTop: '10px', marginBottom: '10px'}}>
                <span className="total-label">Total de crianças encontradas: {sortedCustomers.length}</span>
            </div>

            {sortedCustomers.length > 0 ? (
                <table style={{width: '100%', maxHeight: '100%'}}>
                    <thead>
                        <tr>
                            <th style={{cursor: "pointer", minWidth: "150px"}} onClick={() => handleSort('customerName')}>
                                {getSortIcon('customerName')} Cliente
                            </th>
                            <th style={{cursor: "pointer", minWidth: "120px"}} onClick={() => handleSort('kidName')}>
                                {getSortIcon('kidName')} Criança
                            </th>
                            <th style={{cursor: "pointer", minWidth: "120px"}} onClick={() => handleSort('kidBirthDate')}>
                                {getSortIcon('kidBirthDate')} Data Nascimento
                            </th>
                            <th style={{cursor: "pointer", minWidth: "80px", textAlign: 'center'}} onClick={() => handleSort('kidAge')}>
                                {getSortIcon('kidAge')} Idade
                            </th>
                            <th style={{cursor: "pointer", minWidth: "100px", textAlign: 'center'}} onClick={() => handleSort('ageRange')}>
                                {getSortIcon('ageRange')} Faixa Etária
                            </th>
                            <th style={{minWidth: "80px", textAlign: 'center'}}>
                                Contato
                            </th>
                        </tr>
                    </thead>
                    <tbody style={{overflowY: 'auto'}}>
                        {sortedCustomers.map((customer, index) => (
                            <tr key={index}>
                                <td>
                                    <div>{customer.customerName}</div>
                                </td>
                                <td>
                                    <div>{customer.kidName}</div>
                                </td>
                                <td>
                                    <div>{customer.kidBirthDate}</div>
                                </td>
                                <td style={{textAlign: 'center'}}>
                                    <div>{customer.kidAge} anos</div>
                                </td>
                                <td style={{textAlign: 'center'}}>
                                    <div>
                                        <span style={{
                                            backgroundColor: getAgeRangeColor(customer.ageRange),
                                            color: 'white',
                                            padding: '2px 8px',
                                            borderRadius: '12px',
                                            fontSize: '12px'
                                        }}>
                                            {customer.ageRange}
                                        </span>
                                    </div>
                                </td>
                                <td style={{textAlign: 'center'}}>
                                    <div>
                                        <WhatsAppButton phoneNumber={customer.customerPhone} />
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            ) : (
                <div style={{textAlign: 'center', padding: '20px', color: '#666'}}>
                    Nenhuma criança encontrada com os filtros aplicados.
                </div>
            )}
        </>
    );
};

const getAgeRangeColor = (ageRange) => {
    switch (ageRange) {
        case '0-1 ano':
            return '#e74c3c';
        case '1-2 anos':
            return '#e67e22';
        case '3-4 anos':
            return '#f39c12';
        case '5-6 anos':
            return '#f1c40f';
        case '7-9 anos':
            return '#2ecc71';
        case '10-12 anos':
            return '#3498db';
        case '13-15 anos':
            return '#9b59b6';
        case '16+ anos':
            return '#34495e';
        default:
            return '#95a5a6';
    }
};

export default ReportCustomerAgeRange; 